import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { PremiumLocaleProvider } from '@wix/premium-react-text';
import i18n from './i18n/client-i18n';
import { App } from './components/App';
import { AppParams, IAppContext } from './types';
import { FedopsLogger } from 'services/FedopsLogger';
import { create } from '@wix/fedops-logger';
import { SentryLogger } from 'services/SentryLogger';
import * as Sentry from '@sentry/browser';
import { BiLoggerFactory } from 'services/BiLogger/BiLoggerFactory';
import { RootStore } from 'providers';
import { appName } from './constants';
import { Provider as MobxProvider } from 'mobx-react';
import { AppStore } from 'stores/App.store';
import { configure as mobxConfigure } from 'mobx';
import { NotificationStore } from 'components/Notification/Notification.store';
import { Storage } from 'services/Storage/Storage';
import supportChatWidget from '@wix/support-chat-widget-wrapper';
import { PageModeProvider } from 'providers/PageMode/PageMode.provider';
import { initWixRecorder } from 'services/WixRecorder/WixRecorder';
import { GlobalErrorBoundary } from 'components/ErrorBoundaries/GlobalErrorBoundary';

const initialI18n = window.__INITIAL_I18N__;
const experiments = window.__EXPERIMENTS__;
const appParams = window.__APP_PARAMS__ as AppParams;
const {
  eventAggregator,
  userGuid,
  siteGuid,
  referral,
  locale,
  staticsBaseUrl,
  requestParamsMap,
  isMobile,
  isUpgrade,
} = appParams;

initWixRecorder(appParams, experiments);

const getReferralAdditionalInfo = () => {
  return requestParamsMap?.referralAdditionalInfo || '';
};

const biLogger = BiLoggerFactory(
  {
    eventAggregator,
    referralAdditionalInfo: getReferralAdditionalInfo(),
    referer: referral,
    siteGuid,
    newPurchase: isUpgrade ? 'false' : 'true',
  },
  appParams.brand,
);

const fedopsLogger = new FedopsLogger(create(appName, { useBatch: false }));

const appContext: IAppContext = {
  ...appParams,
  fedopsLogger,
  staticsBaseUrl,
  biLogger,
  storage: new Storage(userGuid),
  experiments,
};

SentryLogger.initSentry({ Sentry, context: 'client', appParams });

// TODO: extract it for storybook & main app!
let currencySettingsMap;
const { currenciesSettings, currencySettings, currencyCode } = appContext;
if (currenciesSettings?.length) {
  currencySettingsMap = [];
  currenciesSettings.forEach((settings) => {
    currencySettingsMap[settings.code] = settings;
  });
} else {
  currencySettingsMap = {
    [currencyCode]: currencySettings,
  };
}

const getDomainFromUrl = () => {
  return requestParamsMap?.domainName;
};

mobxConfigure({
  enforceActions: 'observed',
  isolateGlobalState: true,
});

const appStore = new AppStore(appContext);
const notificationStore = new NotificationStore();
const rootStore = new RootStore(appStore, notificationStore);
appStore.storage?.setDomain(getDomainFromUrl());

ReactDOM.render(
  <I18nextProvider i18n={i18n(initialI18n)}>
    <GlobalErrorBoundary fedopsLogger={fedopsLogger} isMobile={isMobile}>
      <ExperimentsProvider options={{ experiments }}>
        <PageModeProvider
          theme={appContext.pageMode.theme}
          template={appContext.pageMode.template}
          locale={locale}
        >
          <PremiumLocaleProvider
            lng={locale}
            currencySettings={currencySettingsMap}
            currencyCode={appContext.currencyCode}
          >
            <MobxProvider rootStore={rootStore}>
              <App />
            </MobxProvider>
          </PremiumLocaleProvider>
        </PageModeProvider>
      </ExperimentsProvider>
    </GlobalErrorBoundary>
  </I18nextProvider>,
  document.getElementById('root'),
);

if (!appParams.isAnonymous) {
  void supportChatWidget.init({
    origin: 'PackagePicker',
    msid: appContext.siteGuid,
    sessionId: appContext.eventAggregator,
  });
}
